import { useEffect, useReducer, useRef, useState } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { ColumnMatcher } from "../../models/ColumnMatcher";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import { ExtractedTable } from "../../models/ExtractedTable";
import { HighlightColours } from "../../models/HighlightColors";
import { Hydratable } from "../../models/hydration/Hydratable";
import { RegexMatcher } from "../../models/matchers/RegexMatcher";
import Model from "../../models/Model";
import Page from "../../models/Page";
import { TableModel } from "../../models/TableModel";
import "../../styles/tableEditing/tableEditor.css";
import { SizeBubble } from "../bubbles/SizeBubble";
import { Button } from "../buttons/Button";
import { VisibilityToggle } from "../buttons/VisibilityToggle";
import { HydratableDocError } from "../errorScreens/HydratableDocError";
import { HydratableModelError } from "../errorScreens/HydratableModelError";
import { ExtractedTableViewer } from "../ExtractedTableViewer";
import { Header } from "../Header";
import { useColumnHighlight } from "../hooks/dlfEditing/useColumnHighlight";
import { useAsync } from "../hooks/useAsync";
import { useTimer } from "../hooks/useTimer";
import Icon from "../Icon";
import { TablesNotLicensed } from "../licensing/TablesNotLicensed";
import { useLicenseDetails } from "../licensing/useLicenseDetails";
import { MatcherEditor } from "../matcherEditing/MatcherEditor";
import { ReuseControl } from "../reuse/ReuseControl";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTColumnsHeader,
  TTHighlightColumns,
  TTIgnoreRuleButton,
  TTIgnoreRuleCount,
  TTIgnoreRuleLabel,
  TTTableGenerateReuse,
  TTViewExtractedTable,
} from "../tooltips/ConfigurationTooltips";
import { ColumnEditorList } from "./ColumnEditorList";
import { ColumnTransformList } from "./ColumnTransformList";
import { ColumnValidationList } from "./ColumnValidationList";
import { HighlightableColumnEditor } from "./HighlightableColumnEditor";
import { TableClassification } from "./TableClassification";
import { TableNamesList } from "./TableNameList";

export { TableEditor };

type Props = {
  docTypeConfig?: DocTypeConfig;
  acceleratorDoc: Hydratable<AcceleratorDoc> | undefined;
  model: Hydratable<Model> | undefined;
  modelChanged: (modified: TableModel) => void;
  /**
   * Select the TableTemplate reuse value at this index for this Model. This can avoid setting modelChanged, if
   * generated automatically on first mount.
   */
  changeToReuse: (
    tableName: string,
    reuseIndex: number,
    skipModelChanged?: boolean,
  ) => void;
  view: "extraction" | "transformation" | "validation";
};

const VIEW_EXTRACTED_SESSION = "ACC_TABLEEDITOR_VIEW_EXTRACTED";

/**
 * Component for editing tables in Configuration screen.
 * @param props
 * @returns
 */
const TableEditor = (props: Props) => {
  const generateReuse = async (skipModelChanged?: boolean) => {
    const data = await props
      .acceleratorDoc!.getHydrated()
      .generateTableReuse(selectedTableName);
    if (data?.length > 0) {
      props.changeToReuse(selectedTableName, 1, skipModelChanged);
    } else {
      // show Table Reuse errors !
    }
    return data;
  };

  const acceleratorDoc = props.acceleratorDoc?.isHydrated()
    ? props.acceleratorDoc.getHydrated()
    : undefined;
  const model = props.model?.isHydrated()
    ? props.model.getHydrated()
    : undefined;

  const mounted = useRef(false);
  const [licenseDetails] = useLicenseDetails();

  const [selectedTableName, setSelectedTableName] = useState<string>(
    props.docTypeConfig?.tables[0]?.name ?? "",
  );

  const tableConfigs = props.docTypeConfig?.tables ?? [];
  const selectedTable = tableConfigs.find(
    (tc) => tc.name === selectedTableName,
  );

  const [showExtractedData, setShowExtractedData] = useReducer(
    (old) => {
      sessionStorage.setItem(VIEW_EXTRACTED_SESSION, !old + "");
      return !old;
    },
    false,
    (_) => {
      try {
        const x = sessionStorage.getItem(VIEW_EXTRACTED_SESSION);
        return x === "true";
      } catch (e) {
        return false;
      }
    },
  );

  const getPageSpansById = useRef(
    acceleratorDoc?.getPageSpansById.bind(acceleratorDoc),
  ).current!;

  const [generatedReuse, generateReuseTrigger, generatingReuse] = useAsync(
    generateReuse,
    null,
  );
  const [reusable, setReusable] = useState(
    model?.getTableModel(selectedTableName)?.generateReuse ?? false,
  );
  const [showOffset, setShowOffset] = useState(false);
  /* Stores column names that are highlighted */
  const [highlights, setHighlights] = useState<string[]>([]);

  // fetch tableModel here so columnHighlight hook can access this TableModel. error thrown further down in function if missing
  const tableModel: TableModel | undefined =
    model?.getTableModel(selectedTableName);
  const [showOffsetToggle, setShowOffsetToggle] = useState(
    tableModel?.handleOffset == true,
  );

  /**
   * Stores open Validation or Transformation editor
   */
  const [open, setOpen] = useState<string | undefined>(undefined);

  /* Reselect selected Table when DocTypeConfig changes */
  useEffect(() => {
    const foundTable = props.docTypeConfig?.tables.find(
      (x) => x.name === selectedTableName,
    );
    if (!foundTable) {
      setSelectedTableName(props.docTypeConfig?.tables[0]?.name ?? "");
    }
  }, [props.docTypeConfig]);

  useEffect(() => setHighlights([]), [selectedTableName]);

  /* Render Table Offset highlight */
  useColumnHighlight(
    0,
    "table-offset-" + (tableModel?.startOffset ?? 0),
    tableModel?.startOffset ?? 0,
    tableModel?.startOffsetEnd ?? 0,
    showOffset,
  );

  useEffect(() => {
    // only generate table reuse if Model was Unknown/General, and no table reuse already generated
    if (
      !mounted.current &&
      model?.unknown &&
      acceleratorDoc
        ?.getExtractedTableData(selectedTableName)
        ?.getReuse()
        .filter((x) => x != undefined).length == 0
    ) {
      generateReuseTrigger(true);
      mounted.current = true;
    }
  });

  const [lastTable, setLastTable] = useState(
    acceleratorDoc
      ?.getExtractedTableData(selectedTableName)
      .debug_GetExtracted()?.tableModelId,
  );
  const [lastUpdated] = useTimer(
    async (selected: string, extracted: ExtractedTable | undefined) =>
      extracted,
    100,
    (tableData) => {
      // alert('Called table name list timer');
      if (tableData?.tableModelId !== lastTable) {
        alert("Table changed ");
        setLastTable(tableData.tableModelId);
      }
    },
    [
      selectedTableName,
      acceleratorDoc
        ?.getExtractedTableData(selectedTableName)
        .debug_GetExtracted(),
    ],
  );

  // if no hydratable provided, no doc selected
  if (!acceleratorDoc) {
    return <HydratableDocError acceleratorDoc={props.acceleratorDoc} />;
  }
  if (!model) {
    return <HydratableModelError model={props.model} />;
  }

  if (!props.docTypeConfig) {
    return <span>Invalid document type</span>;
  }
  if (props.docTypeConfig.tables.length === 0) {
    return <span>No tables available in this document type.</span>;
  }

  if (selectedTableName === "") {
    return <span>Invalid table selected.</span>;
  }

  if (!tableModel) {
    throw new Error("TableModel could not be found on Template");
  }

  const extractedData = acceleratorDoc.getExtractedTableData(selectedTable!);

  const allColumnsHighlighted = highlights.length === tableModel.columns.length; //.every(c => c.showColumn);

  return (
    <>
      <TablesNotLicensed details={licenseDetails} />
      <div
        className={"tableEditor"}
        data-view={props.view}
        data-show-extracted={showExtractedData}
      >
        <div className={"top"}>
          <TableNamesList
            selectedTableData={acceleratorDoc
              .getExtractedTableData(selectedTableName)
              .debug_GetExtracted()}
            onChange={(tc) => setSelectedTableName(tc.name)}
            selected={selectedTableName}
            tableConfigs={tableConfigs}
          />

          <div className={"fluffer"} />

          {props.view === "extraction" && (
            <AdvancedTooltip tooltipText={TTTableGenerateReuse}>
              {(tProps) => (
                <ReuseControl
                  tProps={tProps}
                  extractedData={extractedData}
                  generatingReuse={generatingReuse}
                  changeToReuse={(reuseIndex: number) =>
                    props.changeToReuse(selectedTableName, reuseIndex)
                  }
                  generateReuse={() => generateReuseTrigger()}
                />
              )}
            </AdvancedTooltip>
          )}
        </div>

        {/* TODO: extract */}
        {props.view === "extraction" && (
          <>
            <div className={"config"}>
              {/* object to display start and soft/hard stop pattern */}
              <TableClassification
                allowEmpty={tableModel.allowEmpty}
                generateReuse={tableModel.generateReuse}
                defaultMatcherThreshold={props.docTypeConfig.getDefaultMatcherThresold()}
                handleOffset={tableModel.handleOffset}
                startOffset={tableModel.startOffset}
                startOffsetEnd={tableModel.startOffsetEnd}
                startPattern={tableModel.startPattern}
                softStopPattern={tableModel.softStopPattern}
                hardStopPattern={tableModel.stopPattern}
                acceleratorDocUuid={acceleratorDoc.uuid}
                extractedTable={extractedData.debug_GetExtracted()}
                getPageSpansById={getPageSpansById}
                getAllSpans={acceleratorDoc.getAllPageSpans}
                pages={acceleratorDoc.pages}
                updateOffset={(handleOffset, start, end) => {
                  setShowOffset(handleOffset);
                  setShowOffsetToggle(handleOffset);
                  tableModel.handleOffset = handleOffset;
                  tableModel.startOffset = start;
                  tableModel.startOffsetEnd = end;
                  props.modelChanged(tableModel);
                }}
                updateGenerateReuse={(generateReuse) => {
                  tableModel.generateReuse = generateReuse;
                  props.modelChanged(tableModel);
                }}
                updateAllowEmpty={(allowEmpty) => {
                  tableModel.allowEmpty = allowEmpty;
                  props.modelChanged(tableModel);
                }}
                updatePattern={(matcher, type) => {
                  switch (type) {
                    case "start": {
                      tableModel.updateStartPattern(matcher);
                      break;
                    }
                    case "soft": {
                      tableModel.updateSoftStopPattern(matcher);
                      break;
                    }
                    case "hard": {
                      tableModel.updateHardStopPattern(matcher);
                      break;
                    }
                    default: {
                      throw new Error(`Could not update ${type} on TableModel`);
                    }
                  }
                  props.modelChanged(tableModel);
                }}
              />

              <div className={"sizeHeader"}>
                <AdvancedTooltip tooltipText={TTIgnoreRuleLabel}>
                  {(tProps) => <Header {...tProps}>Ignore Rules</Header>}
                </AdvancedTooltip>
                <AdvancedTooltip short tooltipText={TTIgnoreRuleCount}>
                  {(tProps) => (
                    <SizeBubble
                      tProps={tProps}
                      length={tableModel.ignorePatterns.length}
                    />
                  )}
                </AdvancedTooltip>
              </div>

              <div className={"row"}>
                <AdvancedTooltip short tooltipText={TTIgnoreRuleButton}>
                  {(tProps) => (
                    <Button
                      {...tProps}
                      style={{ marginBottom: 5 }}
                      onClick={() => {
                        const matcher = new RegexMatcher({
                          discriminator: "M_REGEX",
                          position: tableModel.ignorePatterns.length,
                          regex: "",
                          reusable: true,
                          threshold:
                            props.docTypeConfig!.getDefaultMatcherThresold(),
                        });
                        tableModel.addIgnoreRule(matcher);
                        props.modelChanged(tableModel);
                      }}
                    >
                      Add Rule
                    </Button>
                  )}
                </AdvancedTooltip>
              </div>

              <div className={"row ignoreRules"}>
                {tableModel.ignorePatterns.map((ignoreMatcher, index) => (
                  <MatcherEditor
                    key={"IGNORE" + ignoreMatcher.getMatcherId() + index}
                    label={
                      ignoreMatcher.getDiscriminator() === "M_REGEX"
                        ? "Regex"
                        : "Fuzzy"
                    }
                    index={index}
                    matcher={ignoreMatcher}
                    defaultMatcherThreshold={props.docTypeConfig!.getDefaultMatcherThresold()}
                    labelColor={"black"}
                    labelBgColor={HighlightColours.getHighlightColor(index)}
                    changedMatcherType={(matcher) => {
                      tableModel.replaceIgnoreRule(matcher, index);
                      props.modelChanged(tableModel);
                    }}
                    update={(matcher, oldMatcherId) => {
                      props.modelChanged(tableModel);
                    }}
                    button={
                      <Button
                        onClick={() => {
                          tableModel.removeIgnoreRule(ignoreMatcher);
                          props.modelChanged(tableModel);
                        }}
                      >
                        <Icon altText={"Delete Ignore Rule"} icon={"trash"} />
                      </Button>
                    }
                  />
                ))}
              </div>
            </div>
            <div className={"row"}>
              <AdvancedTooltip tooltipText={TTColumnsHeader}>
                {(tProps) => <Header {...tProps}>Column Rules</Header>}
              </AdvancedTooltip>
            </div>
            <div className={"row"} data-toggle-row>
              <AdvancedTooltip short tooltipText={TTHighlightColumns}>
                {(tProps) => (
                  <VisibilityToggle
                    tProps={tProps}
                    active={allColumnsHighlighted}
                    onClick={() => {
                      if (allColumnsHighlighted) {
                        tableModel.columns.forEach(
                          (c) => (c.showColumn = false),
                        );
                      } else {
                        tableModel.columns.forEach(
                          (c) => (c.showColumn = true),
                        );
                      }
                      setHighlights(
                        tableModel.columns
                          .filter((x) => x.showColumn)
                          .map((x) => x.columnName),
                      );
                    }}
                  >
                    Highlight Columns
                  </VisibilityToggle>
                )}
              </AdvancedTooltip>

              <AdvancedTooltip short tooltipText={TTViewExtractedTable}>
                {(tProps) => (
                  <VisibilityToggle
                    tProps={tProps}
                    active={showExtractedData}
                    onClick={setShowExtractedData}
                  >
                    View Extracted Table
                  </VisibilityToggle>
                )}
              </AdvancedTooltip>

              {showOffsetToggle && (
                <VisibilityToggle
                  active={showOffset}
                  onClick={() => setShowOffset(!showOffset)}
                >
                  View Offset
                </VisibilityToggle>
              )}
            </div>

            {/* object to display all columns  */}
            {/* {!showExtractedData && ( */}
            <ColumnEditorList
              tableModel={tableModel}
              tableConfig={selectedTable!}
              modelChanged={(tableModel) => props.modelChanged(tableModel)}
            >
              {(columns, update, changeAnchor, reverseAnchor) => (
                <>
                  {columns.map((column, index) => {
                    const columnConfig = selectedTable?.getDocFieldConfig(
                      column.columnName,
                    )!;
                    const correctedIndex = columns.length - 1 - index;
                    return (
                      <HighlightableColumnEditor
                        key={
                          selectedTableName +
                          column.getMatcherIdWithoutRange() +
                          index
                        }
                        showColumn={highlights.includes(column.columnName)}
                        index={correctedIndex}
                        columnConfig={columnConfig}
                        suggestions={[{ label: "Default", regex: ".+" }].concat(
                          columnConfig.validator.validationRules.map((x) => ({
                            label: x.label,
                            regex: x.regex,
                          })),
                        )}
                        highlightKey={column.columnName + column.getMatcherId()}
                        label={column.columnName}
                        labelColor={"black"}
                        labelBgColor={HighlightColours.getHighlightColor(
                          correctedIndex,
                        )}
                        matcher={column}
                        nestedMatcher={column.matcher}
                        update={(matcher: ColumnMatcher) => update(matcher)}
                        onRangeChange={() => {
                          if (
                            column.showColumn &&
                            !highlights.includes(column.columnName)
                          ) {
                            setHighlights(highlights.concat(column.columnName));
                          }
                          update(column);
                        }}
                        onRangeMove={() => {
                          if (
                            column.showColumn &&
                            !highlights.includes(column.columnName)
                          ) {
                            setHighlights(highlights.concat(column.columnName));
                          }
                        }}
                        onAnchorChange={() => changeAnchor(column)}
                        onAnchorReverseChange={(reversed: boolean) =>
                          reverseAnchor(column, reversed)
                        }
                        onHighlightChange={() => {
                          if (column.showColumn) {
                            if (!highlights.includes(column.columnName)) {
                              setHighlights(
                                highlights.concat(column.columnName),
                              );
                            }
                          } else {
                            setHighlights(
                              highlights.filter((x) => x !== column.columnName),
                            );
                          }
                        }}
                      />
                    );
                  })}
                </>
              )}
            </ColumnEditorList>
            {/* )} */}

            {showExtractedData && (
              <div className={"extracted"}>
                <ExtractedTableViewer
                  tableConfig={selectedTable!}
                  extractedTable={extractedData}
                  getPageById={(pageId) =>
                    acceleratorDoc.getPageSpansById(pageId)
                  }
                  highlightPageRow={async (pageId, pRowNumber) => {
                    const page = acceleratorDoc.pages.find(
                      (x) => x.externalPageIdentifier === pageId,
                    );
                    if (!page)
                      throw new Error("Could not find page with id " + pageId);
                    const pSpans = await acceleratorDoc.getPageSpans(
                      page.pageIndex,
                    );
                    return [page, Page.getRow(pSpans, pRowNumber)];
                  }}
                  pageIdToIndex={(pageId) => {
                    return acceleratorDoc.pages.findIndex(
                      (x) => x.externalPageIdentifier === pageId,
                    );
                  }}
                />
              </div>
            )}
          </>
        )}
        {props.view === "transformation" && (
          <ColumnTransformList
            acceleratorDocUuid={acceleratorDoc.uuid}
            tableConfig={selectedTable!}
            tableModel={tableModel}
            model={model}
            fieldConfigs={props.docTypeConfig.fields}
            open={open}
            setOpen={setOpen}
            modelChanged={props.modelChanged}
          />
        )}
        {props.view === "validation" && (
          <ColumnValidationList
            acceleratorDocUuid={acceleratorDoc.uuid}
            tableConfig={selectedTable!}
            tableModel={tableModel}
            model={model}
            open={open}
            setOpen={setOpen}
            modelChanged={props.modelChanged}
          />
        )}
      </div>
    </>
  );
};
